import { Fragment } from "react";

import RenderIf from "common/components/RenderIf";
import {
  DropdownMenuCheckbox,
  DropdownMenuCount,
  DropdownMenuItem,
} from "common/components/ui/DropdownMenu";
import { typedObjectKeys } from "common/helpers/utils";

import {
  AUDIENCE_PROFILE_STATES,
  CANCELLED_TARGET_STATES,
} from "../../../../../constants";

import type {
  AudienceProfile,
  AudienceProfileState,
  CancelledTarget,
  CancelledTargetState,
} from "../../../../../types";

export interface AudienceStateFilterMenuContentProps {
  selectedStates: (AudienceProfileState | CancelledTargetState)[];
  setSelectedStates: (
    selectedGroups: (AudienceProfileState | CancelledTargetState)[],
  ) => void;
  audienceWithCancellation: (AudienceProfile & {
    cancellationDetails?: CancelledTarget;
  })[];
}

export function AudienceStateFilterMenuContent({
  selectedStates,
  setSelectedStates,
  audienceWithCancellation,
}: AudienceStateFilterMenuContentProps) {
  function addStateFilters(
    states: (AudienceProfileState | CancelledTargetState)[],
  ) {
    setSelectedStates([...selectedStates, ...states]);
  }

  function removeStateFilters(
    states: (AudienceProfileState | CancelledTargetState)[],
  ) {
    setSelectedStates(
      selectedStates.filter((state) => !states.includes(state)),
    );
  }

  return (
    <>
      <DropdownMenuItem onSelect={() => setSelectedStates([])}>
        All audience
        {audienceWithCancellation && (
          <DropdownMenuCount>
            {audienceWithCancellation.length}
          </DropdownMenuCount>
        )}
      </DropdownMenuItem>

      {AUDIENCE_PROFILE_STATES.map(({ label, states }) => {
        const isCancelledState = states.includes("cancelled");
        const allStates = isCancelledState
          ? [...states, ...typedObjectKeys(CANCELLED_TARGET_STATES)]
          : states;
        const isChecked = selectedStates.some((selectedState) =>
          allStates.some((state) => selectedState === state),
        );

        return (
          <Fragment key={label}>
            <DropdownMenuItem
              onSelect={() => {
                setSelectedStates(allStates);
              }}
            >
              <DropdownMenuCheckbox
                isChecked={
                  isCancelledState &&
                  isChecked &&
                  !typedObjectKeys(CANCELLED_TARGET_STATES).every((state) =>
                    selectedStates.includes(state),
                  )
                    ? "indeterminate"
                    : isChecked
                }
                onCheckedChange={() => {
                  if (isChecked) {
                    removeStateFilters(allStates);
                  } else {
                    addStateFilters(allStates);
                  }
                }}
              />

              {label}

              {audienceWithCancellation && (
                <DropdownMenuCount>
                  {
                    audienceWithCancellation.filter(({ state }) =>
                      states.includes(state),
                    ).length
                  }
                </DropdownMenuCount>
              )}
            </DropdownMenuItem>

            <RenderIf condition={isCancelledState}>
              {(
                Object.entries(CANCELLED_TARGET_STATES) as [
                  CancelledTargetState,
                  string,
                ][]
              ).map(([subState, subLabel]) => {
                const isSubStateChecked = selectedStates.includes(subState);

                return (
                  <DropdownMenuItem
                    key={subState}
                    isSubItem
                    onSelect={() => {
                      setSelectedStates([subState]);
                    }}
                  >
                    <DropdownMenuCheckbox
                      isChecked={isSubStateChecked}
                      onCheckedChange={() => {
                        if (isSubStateChecked) {
                          removeStateFilters([subState]);
                        } else {
                          addStateFilters([subState]);
                        }
                      }}
                    />

                    {subLabel}

                    {audienceWithCancellation && (
                      <DropdownMenuCount>
                        {
                          audienceWithCancellation.filter(
                            ({ cancellationDetails }) =>
                              cancellationDetails?.transition === subState,
                          ).length
                        }
                      </DropdownMenuCount>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </RenderIf>
          </Fragment>
        );
      })}
    </>
  );
}
